import { createSlice } from "@reduxjs/toolkit";
import { actionGetUserDocList, actionGetUserKybAffiliantedList, actionGetUserKybWalletWishList, KYB_ACC_DETAILS_REQUEST, KYB_ACC_DETAILS_SUCCESS, KYB_ACC_DETAILS_FAILURE, actionGetAccDetails, actionGetCinGSTINDetails, actionGetChecklist, actionGetDirectorInfoMetadata, actionGetBankDetails, actionGetAllDocumentsDownloadUrl, actionGetKycCycleHistory } from "../action/kybOnboardingAction";

const initialState = {
  userDocList: null,
  loading: false,
  errorUserDocList: null,
  formStep: "company_info",
  currentStep: 0,
  
  selectedAccount: {
    id: null,
    email: null,
  },

  affiliatedEntityData: null,
  errorAffiliatedEntityData: null,
  // loadingAffiliatedEntityData: false,

  walletWishListData: null,
  errorWalletWishListData: null,
  // loadingWalletWishListData: false,

  accDetails: null,
  accDetailsError: null,

  cinGstinDetails: null,
  cinGstinDetailsError: null,

  bankDetails: null,
  bankDetailsError: null,

  checklistData: null,
  checklistError: null,

  directorInfoMetadata: null,
  directorInfoMetadataError: null,

  downloadDocumentsByAccountId: {},

  otherDeclarationsChecklist: [],

  kycCycleHistory: null,
  kycCycleError: null,
  kycCycleLoading: false,
};

const kybOnboardingSlice = createSlice({
  name: "kybOnboarding",
  initialState,
  reducers: {
    clearUserDocList(state) {
      state.userDocList = null;
      state.errorUserDocList = null;
      state.loadingUserDocList = false;
    },
    saveCurStep: (state, { payload }) => {
      state.currentStep = payload;
    },
    saveFormStep: (state, { payload }) => {
      state.formStep = payload;
    },
    saveSelectedAccount: (state, { payload }) => {
      state.selectedAccount = payload;
    },
    setOtherDeclarationsChecklist: (state, { payload }) => {
      state.otherDeclarationsChecklist = Array.isArray(payload) ? payload : [];
    },
    resetOtherDeclarationsChecklist: (state) => {
      state.otherDeclarationsChecklist = [];
    },
  },
  extraReducers: (builder) => {
    // -------- USER DOC LIST --------
    builder
      .addCase(actionGetUserDocList.pending, (state) => {
        state.loading = true;
        state.errorUserDocList = null;
      })
      .addCase(actionGetUserDocList.fulfilled, (state, action) => {
        state.loading = false;
        state.userDocList = action.payload;
      })
      .addCase(actionGetUserDocList.rejected, (state, action) => {
        state.loading = false;
        state.errorUserDocList =
          action.payload ?? action.error?.message ?? "Request failed";
      })

      // -------- AFFILIATED ENTITY --------
      .addCase(actionGetUserKybAffiliantedList.pending, (state) => {
        state.loading = true;
        state.errorAffiliatedEntityData = null;
      })
      .addCase(actionGetUserKybAffiliantedList.fulfilled, (state, action) => {
        state.affiliatedEntityData = action.payload ?? null;
        state.loading = false;
      })
      .addCase(actionGetUserKybAffiliantedList.rejected, (state, action) => {
        state.loading = false;
        state.errorAffiliatedEntityData =
          action.payload ?? action.error?.message ?? "Request failed";
      })
      
      // -------- WALLET WISHLIST --------
      .addCase(actionGetUserKybWalletWishList.pending, (state) => {
        state.loading = true;
        state.errorWalletWishListData = null;
      })
      .addCase(actionGetUserKybWalletWishList.fulfilled, (state, action) => {
        state.walletWishListData = action.payload ?? null;
        state.loading = false;
      })
      .addCase(actionGetUserKybWalletWishList.rejected, (state, action) => {
        state.loading = false;
        state.errorWalletWishListData =
          action.payload ?? action.error?.message ?? "Request failed";
      })
      // -------- ACCOUNT DETAILS --------
      .addCase(actionGetAccDetails.pending, (state) => {
        state.loading = true;
        state.accDetailsError = null;
      })
      .addCase(actionGetAccDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.accDetails = action.payload;
      })
      .addCase(actionGetAccDetails.rejected, (state, action) => {
        state.loading = false;
        state.accDetailsError =
          action.error || action.payload || "Failed to fetch account details";
      })
      // -------- CIN/GSTIN DETAILS --------
      .addCase(actionGetCinGSTINDetails.pending, (state) => {
        state.loading = true;
        state.cinGstinDetailsError = null;
      })
      .addCase(actionGetCinGSTINDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.cinGstinDetails = action.payload;
      })
      .addCase(actionGetCinGSTINDetails.rejected, (state, action) => {
        state.loading = false;
        state.cinGstinDetailsError =
          action.payload ?? action.error?.message ?? "Failed to fetch CIN/GSTIN details";
      })

      // -------- BANK DETAILS --------
      .addCase(actionGetBankDetails.pending, (state) => {
        state.loading = true;
        state.bankDetailsError = null;
      })
      .addCase(actionGetBankDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.bankDetails = action.payload;
      })
      .addCase(actionGetBankDetails.rejected, (state, action) => {
        state.loading = false;
        state.bankDetailsError =
          action.payload ?? action.error?.message ?? "Failed to fetch bank details";
      })

      // -------- CHECKLIST --------
      .addCase(actionGetChecklist.pending, (state) => {
        state.loading = true;
        state.checklistError = null;
      })
      .addCase(actionGetChecklist.fulfilled, (state, action) => {
        state.loading = false;
        state.checklistData = action.payload ?? null;
      })
      .addCase(actionGetChecklist.rejected, (state, action) => {
        state.loading = false;
        state.checklistError =
          action.payload ?? action.error?.message ?? "Failed to fetch checklist";
      })

      // -------- DIRECTOR METADATA --------
      .addCase(actionGetDirectorInfoMetadata.pending, (state) => {
        state.loading = true;
        state.directorInfoMetadataError = null;
      })
      .addCase(actionGetDirectorInfoMetadata.fulfilled, (state, action) => {
        state.loading = false;
        state.directorInfoMetadata = action.payload ?? null;
      })
      .addCase(actionGetDirectorInfoMetadata.rejected, (state, action) => {
        state.loading = false;
        state.directorInfoMetadataError =
          action.payload ?? action.error?.message ?? "Failed to fetch director metadata";
      });

    // -------- DOWNLOAD ALL DOCUMENTS (PRESIGNED ZIP URL) --------
    builder
      .addCase(actionGetAllDocumentsDownloadUrl.pending, (state, action) => {
        const accountId =
          typeof action.meta.arg === "object" && action.meta.arg?.accountId != null
            ? action.meta.arg.accountId
            : action.meta.arg;
        state.downloadDocumentsByAccountId[accountId] = {
          loading: true,
          url: state.downloadDocumentsByAccountId?.[accountId]?.url ?? null,
          error: null,
        };
      })
      .addCase(actionGetAllDocumentsDownloadUrl.fulfilled, (state, action) => {
        const accountId =
          typeof action.meta.arg === "object" && action.meta.arg?.accountId != null
            ? action.meta.arg.accountId
            : action.meta.arg;
        const payload = action.payload ?? {};
        const url =
          payload.url ||
          payload.download_url ||
          payload.presigned_url ||
          payload.file_url ||
          payload.zip_url ||
          null;
        state.downloadDocumentsByAccountId[accountId] = {
          loading: false,
          url,
          error: null,
        };
      })
      .addCase(actionGetAllDocumentsDownloadUrl.rejected, (state, action) => {
        const accountId =
          typeof action.meta.arg === "object" && action.meta.arg?.accountId != null
            ? action.meta.arg.accountId
            : action.meta.arg;
        state.downloadDocumentsByAccountId[accountId] = {
          loading: false,
          url: state.downloadDocumentsByAccountId?.[accountId]?.url ?? null,
          error: action.payload ?? action.error?.message ?? "Request failed",
        };
      });

    // -------- GET KYC CYCLE HISTORY --------
    builder
      .addCase(actionGetKycCycleHistory.pending, (state) => {
        state.kycCycleLoading = true;
        state.kycCycleError = null;
      })
      .addCase(actionGetKycCycleHistory.fulfilled, (state, action) => {
        state.kycCycleLoading = false;
        state.kycCycleHistory = action.payload ?? null;
      })
      .addCase(actionGetKycCycleHistory.rejected, (state, action) => {
        state.kycCycleLoading = false;
        state.kycCycleError = action.payload ?? action.error?.message ?? "Failed to fetch cycle history";
      });
  },
});

export const {
  clearUserDocList,
  saveSelectedAccount,
  saveCurStep,
  saveFormStep,
  setOtherDeclarationsChecklist,
  resetOtherDeclarationsChecklist,
} = kybOnboardingSlice.actions;
export default kybOnboardingSlice.reducer;
